import React, { useState, useEffect } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from './ui/dialog';
import { ScrollArea } from './ui/scroll-area';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { Calendar, User, Building, X, Download, FileText } from 'lucide-react';
import axios from 'axios';
import { useToast } from '../hooks/use-toast';

const DisclaimerAcceptancesView = ({ isOpen, onClose, adminToken }) => {
  const [acceptances, setAcceptances] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedAcceptance, setSelectedAcceptance] = useState(null);
  const [showSignatureModal, setShowSignatureModal] = useState(false);
  const { toast } = useToast();

  const backendUrl = process.env.REACT_APP_BACKEND_URL || '';

  useEffect(() => {
    if (isOpen) {
      fetchAcceptances();
    }
  }, [isOpen]);

  const fetchAcceptances = async () => {
    try {
      setLoading(true);
      
      const response = await axios.get(
        `${backendUrl}/api/admin/disclaimer-acceptances`,
        {
          headers: { Authorization: `Bearer ${adminToken}` }
        }
      );
      
      if (response.data.success) {
        setAcceptances(response.data.acceptances);
      }
    } catch (error) {
      console.error('Fetch disclaimer acceptances error:', error);
      toast({
        title: "Error",
        description: "Failed to load disclaimer acceptances",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    return date.toLocaleString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const viewSignature = (acceptance) => {
    setSelectedAcceptance(acceptance);
    setShowSignatureModal(true);
  };

  const downloadSignedDocument = (acceptance) => {
    // Create a simple HTML document with the signature
    const htmlContent = `
      <!DOCTYPE html>
      <html>
      <head>
        <title>Signed Disclaimer - ${acceptance.practiceName}</title>
        <style>
          body { font-family: Arial, sans-serif; padding: 40px; max-width: 800px; margin: 0 auto; }
          h1 { color: #2563eb; text-align: center; }
          .info { margin: 20px 0; padding: 15px; background: #f3f4f6; border-radius: 5px; }
          .signature-section { margin-top: 30px; border-top: 2px solid #e5e7eb; padding-top: 20px; }
          .signature-image { border: 2px solid #d1d5db; padding: 10px; background: white; max-width: 400px; }
          .footer { margin-top: 40px; text-align: center; font-size: 12px; color: #6b7280; }
        </style>
      </head>
      <body>
        <h1>Informed Consent Disclaimer - Digitally Signed</h1>
        
        <div class="info">
          <strong>Practice:</strong> ${acceptance.practiceName}<br>
          <strong>Signed By:</strong> ${acceptance.signerName}<br>
          <strong>Title:</strong> ${acceptance.signerTitle}<br>
          <strong>Date Signed:</strong> ${formatDate(acceptance.acceptedAt)}<br>
          <strong>Email:</strong> ${acceptance.acceptedByEmail}
        </div>

        <div class="signature-section">
          <h3>Digital Signature:</h3>
          <div class="signature-image">
            <img src="${acceptance.signatureData}" alt="Digital Signature" style="width: 100%;">
          </div>
        </div>

        <div class="footer">
          <p>This is a digitally signed legal document.<br>
          Document ID: ${acceptance.id}<br>
          Generated by PractCom System</p>
        </div>
      </body>
      </html>
    `;

    // Create blob and download
    const blob = new Blob([htmlContent], { type: 'text/html' });
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `signed_disclaimer_${acceptance.practiceName.replace(/[^a-z0-9]/gi, '_')}_${new Date(acceptance.acceptedAt).toISOString().split('T')[0]}.html`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(url);

    toast({
      title: "Downloaded",
      description: "Signed disclaimer document downloaded successfully",
    });
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-5xl max-h-[90vh]">
        <DialogHeader>
          <div className="flex items-center justify-between">
            <DialogTitle className="text-xl font-bold">
              Informed Consent Disclaimer Acceptances
            </DialogTitle>
            <Button
              variant="ghost"
              size="sm"
              onClick={onClose}
              className="h-8 w-8 p-0"
            >
              <X className="h-4 w-4" />
            </Button>
          </div>
          <p className="text-sm text-gray-500">
            Practices that have accepted the informed consent disclaimer
          </p>
        </DialogHeader>

        {loading ? (
          <div className="flex items-center justify-center py-12">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
          </div>
        ) : (
          <ScrollArea className="h-[500px]">
            <div className="space-y-3">
              {acceptances.length === 0 ? (
                <div className="text-center py-12 text-gray-500">
                  <p>No disclaimer acceptances recorded yet</p>
                </div>
              ) : (
                acceptances.map((acceptance) => (
                  <div
                    key={acceptance.id}
                    className="border rounded-lg p-4 hover:bg-gray-50 transition-colors"
                  >
                    <div className="flex items-start justify-between">
                      <div className="flex-1 space-y-2">
                        <div className="flex items-center gap-2">
                          <Building className="h-4 w-4 text-blue-600" />
                          <span className="font-semibold text-lg">
                            {acceptance.practiceName}
                          </span>
                          <Badge variant="outline" className="bg-green-50 text-green-700 border-green-200">
                            Accepted
                          </Badge>
                        </div>
                        
                        <div className="grid grid-cols-2 gap-4 text-sm">
                          <div className="flex items-center gap-2">
                            <User className="h-4 w-4 text-gray-400" />
                            <span className="text-gray-600">Signer:</span>
                            <span className="font-medium">{acceptance.signerName}</span>
                          </div>
                          
                          <div className="flex items-center gap-2">
                            <span className="text-gray-600">Title:</span>
                            <span className="font-medium">{acceptance.signerTitle}</span>
                          </div>
                          
                          <div className="flex items-center gap-2">
                            <Calendar className="h-4 w-4 text-gray-400" />
                            <span className="text-gray-600">Signed:</span>
                            <span className="font-medium">{formatDate(acceptance.acceptedAt)}</span>
                          </div>
                          
                          <div className="flex items-center gap-2">
                            <span className="text-gray-600">Logged by:</span>
                            <span className="font-medium">{acceptance.acceptedByName}</span>
                          </div>
                          
                          <div className="flex items-center gap-2 col-span-2">
                            <span className="text-gray-600">Email:</span>
                            <span className="font-medium text-blue-600">{acceptance.acceptedByEmail}</span>
                          </div>
                          
                          {acceptance.ipAddress && (
                            <div className="flex items-center gap-2 col-span-2">
                              <span className="text-gray-600">IP Address:</span>
                              <span className="font-mono text-xs">{acceptance.ipAddress}</span>
                            </div>
                          )}
                        </div>
                        
                        <div className="flex gap-2 mt-4">
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => viewSignature(acceptance)}
                          >
                            <FileText className="h-4 w-4 mr-2" />
                            View Signature
                          </Button>
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => downloadSignedDocument(acceptance)}
                          >
                            <Download className="h-4 w-4 mr-2" />
                            Download Signed Document
                          </Button>
                        </div>
                      </div>
                    </div>
                  </div>
                ))
              )}
            </div>
          </ScrollArea>
        )}

        <div className="flex justify-between items-center pt-4 border-t">
          <div className="text-sm text-gray-500">
            Total acceptances: <span className="font-semibold">{acceptances.length}</span>
          </div>
          <Button variant="outline" onClick={onClose}>
            Close
          </Button>
        </div>
      </DialogContent>

      {/* Signature View Modal */}
      {showSignatureModal && selectedAcceptance && (
        <Dialog open={showSignatureModal} onOpenChange={setShowSignatureModal}>
          <DialogContent className="max-w-2xl">
            <DialogHeader>
              <DialogTitle>Digital Signature</DialogTitle>
            </DialogHeader>
            
            <div className="space-y-4">
              <div className="bg-gray-50 p-4 rounded-lg">
                <div className="grid grid-cols-2 gap-3 text-sm">
                  <div>
                    <span className="text-gray-600">Practice:</span>
                    <p className="font-semibold">{selectedAcceptance.practiceName}</p>
                  </div>
                  <div>
                    <span className="text-gray-600">Signer:</span>
                    <p className="font-semibold">{selectedAcceptance.signerName}</p>
                  </div>
                  <div>
                    <span className="text-gray-600">Title:</span>
                    <p className="font-semibold">{selectedAcceptance.signerTitle}</p>
                  </div>
                  <div>
                    <span className="text-gray-600">Date Signed:</span>
                    <p className="font-semibold">{formatDate(selectedAcceptance.acceptedAt)}</p>
                  </div>
                </div>
              </div>

              <div className="border-2 border-gray-300 rounded-lg p-4 bg-white">
                <p className="text-sm text-gray-600 mb-2">Signature:</p>
                <img 
                  src={selectedAcceptance.signatureData} 
                  alt="Digital Signature"
                  className="w-full max-w-md mx-auto"
                />
              </div>

              <div className="text-xs text-gray-500 text-center">
                Document ID: {selectedAcceptance.id}
              </div>
            </div>

            <div className="flex gap-2 justify-end">
              <Button
                variant="outline"
                onClick={() => downloadSignedDocument(selectedAcceptance)}
              >
                <Download className="h-4 w-4 mr-2" />
                Download
              </Button>
              <Button onClick={() => setShowSignatureModal(false)}>
                Close
              </Button>
            </div>
          </DialogContent>
        </Dialog>
      )}
    </Dialog>
  );
};

export default DisclaimerAcceptancesView;
